import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { FileDownload, TableView } from "@mui/icons-material";
import { useFormik } from "formik";
import moment from "moment";
import AuthContext from "../../../context/AuthContext";
import LoadingBackdrop from "../../LoadingBackdrop";
import AdminFields from "../../adminFields/AdminFields";
import { useSelector, useDispatch } from "react-redux";
import {
  setApplyChanges,
  setWasUsed,
  setReset,
} from "../../adminFields/AdminFields.slice";
import AdminFieldsModal from "../../adminFields/AdminFields.modal";
import ListIcon from "@mui/icons-material/List";
import TextField from "../../textField/TextField";
import DateField from "../../dateField/DateField";
import CurrencySelectField from "../../selectField/CurrencySelectField";
import { showSnackbar } from "../../snackbar/Snackbar.slice";
import { TransactionSearchReportFormRoot } from "./TransactionSearchReport.styles";
import TransactionSearchReportTransactionsTable from "./TransactionSearchReport.transactionsTable";
import { ALL_TERMINALS_VALUE } from "./TransactionSearchReport.utils";
import {
  buildTransactionSearchExportRows,
  writeTransactionSearchExcel,
} from "./TransactionSearchReport.excel";
import { searchTransactionReport } from "./TransactionSearchReport.thunk";
import TransactionExportDialog from "./TransactionExport.dialog";
import {
  transactionSearchReportInitialValues,
  transactionSearchReportValidationSchema,
} from "./TransactionSearchReport.resources";

const TransactionSearchReport = () => {
  const dispatch = useDispatch();
  const {
    token,
    acquirerData,
    userInfo: { "acquirer-rol-data": userRole },
  } = useContext(AuthContext);
  const { merchantID: defaultMerchantID, currency_code: defaultCurrency } =
    acquirerData || {};
  const [reportTerminalFilter, setReportTerminalFilter] =
    useState(ALL_TERMINALS_VALUE);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    applyChanges,
    acquirerId,
    isoID: storeIsoID,
    wasUsed,
    reset,
    acquirerCurrencyId,
  } = useSelector((state) => state.adminFields);
  const { status, retryCount, responseData } = useSelector(
    (state) => state.transactionSearchReport,
  );
  const [modalType, setModalType] = useState("merchant");
  const [modalOpen, setModalOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const {
    values,
    handleSubmit,
    isSubmitting,
    handleChange,
    handleBlur,
    errors,
    touched,
    setSubmitting,
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormik({
    initialValues: {
      ...transactionSearchReportInitialValues,
      merchantID:
        defaultMerchantID || transactionSearchReportInitialValues.merchantID,
      currency:
        defaultCurrency || transactionSearchReportInitialValues.currency,
    },
    validationSchema: transactionSearchReportValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    enableReinitialize: true,
    onSubmit: (formValues) => {
      setSubmitting(true);
      dispatch(
        searchTransactionReport({
          merchantID: formValues.merchantID,
          terminalID: formValues.terminalID,
          currency: formValues.currency,
          amount: formValues.amount,
          exchangeName: formValues.exchangeName,
          requestDate: formValues.requestDate,
          token,
          acquirerData,
          wasUsed,
          acquirerId,
        }),
      );
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (reset) {
      setFieldValue("currency", acquirerData?.currency_code || "");
      dispatch(setReset(false));
    }
  }, [reset, acquirerData?.currency_code, setFieldValue, dispatch]);

  useEffect(() => {
    if (applyChanges) {
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(true));
      setFieldValue("currency", acquirerCurrencyId || "");
    }
  }, [applyChanges, acquirerCurrencyId, setFieldValue, dispatch]);

  useEffect(() => {
    if (wasUsed && storeIsoID === "" && acquirerId === "") {
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(false));
      setFieldValue("currency", acquirerData?.currency_code || "");
    }
  }, [
    wasUsed,
    storeIsoID,
    acquirerId,
    acquirerData?.currency_code,
    setFieldValue,
    dispatch,
  ]);

  useEffect(() => {
    if (defaultMerchantID && values.merchantID === "") {
      setFieldValue("merchantID", defaultMerchantID);
    }
    if (
      defaultCurrency &&
      !wasUsed &&
      (values.currency === "" || values.currency !== defaultCurrency)
    ) {
      setFieldValue("currency", defaultCurrency);
    }
  }, [
    defaultMerchantID,
    defaultCurrency,
    wasUsed,
    values.merchantID,
    values.currency,
    setFieldValue,
  ]);

  useEffect(() => {
    if (values.requestDate) {
      validateForm();
    }
  }, [values.requestDate, validateForm]);

  useEffect(() => {
    if (responseData) {
      setReportTerminalFilter(ALL_TERMINALS_VALUE);
    }
  }, [responseData]);

  const handleOpenModalMerchant = () => {
    setModalType("merchant");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType(null);
  };

  const handleSelectMerchant = (selectedMerchant) => {
    setFieldValue("merchantID", selectedMerchant.merchantID);
  };

  const handleExportExcel = () => {
    const rows = buildTransactionSearchExportRows(
      responseData,
      reportTerminalFilter,
    );
    if (!rows.length) {
      dispatch(
        showSnackbar({
          message: "No transactions available to export.",
          severity: "warning",
        }),
      );
      return;
    }
    writeTransactionSearchExcel(rows);
  };

  const isLoading = status === "loading";

  return (
    <>
      <LoadingBackdrop loading={isLoading} retryCount={retryCount} />
      <AdminFields hideIsoField />
      {modalOpen && modalType && (
        <AdminFieldsModal
          type={modalType}
          open={modalOpen}
          onClose={handleCloseModal}
          onSelectMerchant={handleSelectMerchant}
        />
      )}
      <TransactionSearchReportFormRoot component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              id="merchantID"
              name="merchantID"
              fullWidth
              label="Merchant ID"
              value={values.merchantID}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              disabled={["merchant", "merchant-iso"].includes(userRole)}
              error={errors.merchantID}
              touched={touched.merchantID}
              InputLabelProps={{
                shrink: Boolean(values.merchantID),
              }}
              InputProps={{
                endAdornment: [
                  "acquirer",
                  "acquirer-iso",
                  "ISO",
                  "admin",
                ].includes(userRole) && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="open Merchant list"
                      onClick={handleOpenModalMerchant}
                    >
                      <ListIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              id="terminalID"
              name="terminalID"
              fullWidth
              label="Terminal ID"
              value={values.terminalID}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.terminalID}
              touched={touched.terminalID}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <CurrencySelectField
              id="currency"
              name="currency"
              fullWidth
              label="Currency Code"
              value={values.currency}
              required
              maxLength={3}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.currency}
              touched={touched.currency}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              id="amount"
              name="amount"
              fullWidth
              label="Amount"
              type="number"
              value={values.amount}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.amount}
              touched={touched.amount}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              id="exchangeName"
              name="exchangeName"
              fullWidth
              label="Exchange Name"
              value={values.exchangeName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.exchangeName}
              touched={touched.exchangeName}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <DateField
              id="requestDate"
              name="requestDate"
              fullWidth
              label="Request Date"
              showTimezoneLabel
              value={values.requestDate}
              onChange={handleChange}
              onBlur={handleBlur}
              setFieldTouched={setFieldTouched}
              required
              error={errors.requestDate}
              touched={touched.requestDate}
              minDate={moment().subtract(20, "years")}
              maxDate={moment()}
            />
          </Grid>

          <Grid item xs={12} textAlign={isMobile ? "center" : "left"}>
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={isSubmitting || isLoading}
              sx={{
                marginRight: isMobile ? 0 : 2,
                marginBottom: isMobile ? 2 : 0,
              }}
            >
              {isLoading ? "GENERATING..." : "GENERATE"}
            </Button>
            <Button
              variant="contained"
              type="button"
              startIcon={<FileDownload />}
              onClick={handleExportExcel}
              size="large"
              sx={{ marginLeft: isMobile ? 0 : 2 }}
              disabled={!responseData}
            >
              DOWNLOAD
            </Button>
            <Button
              variant="outlined"
              type="button"
              startIcon={<TableView />}
              onClick={() => setExportOpen(true)}
              size="large"
              sx={{
                marginLeft: isMobile ? 0 : 2,
                marginTop: isMobile ? 2 : 0,
              }}
            >
              EXPORT TRANSACTIONS
            </Button>
          </Grid>
        </Grid>
      </TransactionSearchReportFormRoot>
      {exportOpen && (
        <TransactionExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          merchantID={values.merchantID}
          terminalID={values.terminalID}
          isoID={acquirerData?.isoID}
          token={token}
          acquirerName={acquirerData?.acquirerName}
          acquirerID={wasUsed ? acquirerId : acquirerData?.acquirerId}
        />
      )}
      {responseData && (
        <TransactionSearchReportTransactionsTable
          data={responseData}
          terminalFilter={reportTerminalFilter}
          onTerminalFilterChange={setReportTerminalFilter}
        />
      )}
    </>
  );
};

export default TransactionSearchReport;
